import { useCallback, useMemo } from 'react';

import type {
  ColDef,
  ICellRendererParams,
  RowSelectedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import { Button } from '@/components/ui/button';
import { useDefaultGridOptions } from '@/grid';

import type { FreshStrawRow } from '../FreshStrawActionPanel/payload';
import {
  entryTotal,
  FertilizationLike,
  formatPartnershipSummary,
  getOrInitRow,
} from './shared';

export interface DesktopGridProps {
  rows: FertilizationLike[];
  selectedRows: FreshStrawRow[];
  loading: boolean;
  onToggleRow: (fertilizationId: string) => void;
  onOpenStageGrade: (fertilizationId: string) => void;
  onOpenFertEdit: (fertilizationId: string) => void;
}

type GridRow = FertilizationLike & {
  _entryTotal: number;
  _partnershipSummary: string | null;
};

export function DesktopGrid({
  rows,
  selectedRows,
  loading,
  onToggleRow,
  onOpenStageGrade,
  onOpenFertEdit,
}: DesktopGridProps) {
  const defaultGridOptions = useDefaultGridOptions();
  const gridRows: GridRow[] = useMemo(
    () =>
      rows.map((f) => {
        const id = f.fertilizationId ?? '';
        const row = getOrInitRow(selectedRows, id);
        return {
          ...f,
          _entryTotal: entryTotal(row.entries),
          _partnershipSummary: formatPartnershipSummary(f.partnership),
        };
      }),
    [rows, selectedRows],
  );

  const selectedIds = useMemo(
    () => new Set(selectedRows.map((r) => r.fertilizationId)),
    [selectedRows],
  );

  const columnDefs = useMemo<ColDef<GridRow>[]>(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 42,
        pinned: 'left',
        sortable: false,
        filter: false,
        resizable: false,
      },
      {
        field: 'mating',
        headerName: 'Mating',
        flex: 2,
        minWidth: 180,
      },
      {
        field: 'donorName',
        headerName: 'Donor',
        flex: 1,
        minWidth: 120,
      },
      {
        field: '_partnershipSummary',
        headerName: 'Partnership',
        flex: 2,
        minWidth: 180,
        tooltipField: '_partnershipSummary',
      },
      {
        field: 'origShipped',
        headerName: 'Lab Shp',
        width: 90,
        type: 'numericColumn',
      },
      {
        field: 'clientShipped',
        headerName: 'Ship Client',
        width: 100,
        type: 'numericColumn',
      },
      {
        field: 'clientTransfer',
        headerName: 'Client Trf',
        width: 90,
        type: 'numericColumn',
      },
      {
        field: 'clientFrozen',
        headerName: 'Client Frz',
        width: 90,
        type: 'numericColumn',
      },
      {
        field: 'clientDiscarded',
        headerName: 'Client Dc',
        width: 90,
        type: 'numericColumn',
      },
      {
        field: '_entryTotal',
        headerName: 'S/G Entered',
        width: 110,
        type: 'numericColumn',
      },
      {
        headerName: '',
        width: 230,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          const id = params.data?.fertilizationId ?? '';
          return (
            <div className="flex gap-1">
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={() => onOpenStageGrade(id)}
              >
                Stage / Grade
              </Button>
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={() => onOpenFertEdit(id)}
              >
                Edit
              </Button>
            </div>
          );
        },
      },
    ],
    [onOpenStageGrade, onOpenFertEdit],
  );

  const onRowSelected = useCallback(
    (event: RowSelectedEvent<GridRow>) => {
      const id = event.data?.fertilizationId;
      if (!id) return;
      const alreadySelected = selectedIds.has(id);
      const nowSelected = event.node.isSelected();
      if (alreadySelected !== nowSelected) {
        onToggleRow(id);
      }
    },
    [onToggleRow, selectedIds],
  );

  // Push selection state INTO ag-grid after selection changes from outside
  // (e.g. after a "clear selection" on save). Uses onGridReady/setSelected.
  const syncSelection = useCallback(
    (api: import('ag-grid-community').GridApi<GridRow>) => {
      api.forEachNode((node) => {
        const id = node.data?.fertilizationId ?? '';
        const shouldBeSelected = selectedIds.has(id);
        if (node.isSelected() !== shouldBeSelected) {
          node.setSelected(shouldBeSelected, false, 'api');
        }
      });
    },
    [selectedIds],
  );

  return (
    <div className="ag-theme-alpine ag-compact min-h-0 flex-1 overflow-hidden">
      <AgGridReact<GridRow>
        gridOptions={defaultGridOptions}
        rowData={gridRows}
        columnDefs={columnDefs}
        loading={loading}
        getRowId={(params) => params.data.fertilizationId ?? ''}
        rowSelection="multiple"
        suppressRowClickSelection
        onRowSelected={onRowSelected}
        onGridReady={(e) => syncSelection(e.api)}
        onModelUpdated={(e) => syncSelection(e.api)}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true,
        }}
        headerHeight={28}
        rowHeight={32}
        overlayNoRowsTemplate="No fertilizations match the current filters"
      />
    </div>
  );
}
